<script setup lang="ts">
/**
 * Singbac Incorporation (Pre) — FORM 45: Consent to Act as Director & Statement of
 * Non-Disqualification (Companies Act 1967, section 173C(a)). 이사별 1부(패키지에서 dir_{id}/ 스코프).
 *
 * · 조건부(원문 * "Delete where inapplicable")는 클릭 토글 → fill 'yes'/'no' → docx [[if …]]:
 *     (iv) 1993년 이전 조항  = has_pre_1993_history
 *     (ba) 노미니 디렉터      = is_nominee_director
 *     (bb) Employment Pass    = is_employment_pass_holder
 *     (c)(i)/(ii) 읽음/통역    = needs_interpreter (on=통역, 통역정보 노출)
 * · 방대한 결격 조항(v: A~G, vi: A~H)은 미리보기에선 요약 표기(생성 docx 는 전문 포함).
 * · 서명 블록·통역 정보는 [[변수]] → CddVarField.
 */
import { computed, inject } from 'vue';
import CddVarField from './cdd/CddVarField.vue';
import SgIncPreSigners from './SgIncPreSigners.vue';
import { useCddFill, DOC_SCOPE } from './cdd/useCddFill';
import './allotDoc.css';

const fill = useCddFill();
// DocScope(dir_{id}/) 안에서 조건부 토글 키도 이사별로 분리 — CddVarField 와 동일하게 scope + key.
const scope = inject(DOC_SCOPE, '');
const skey = (k: string) => scope + k;

function isOn(key: string): boolean {
  const v = String(fill.values[skey(key)] ?? '').trim().toLowerCase();
  return v === 'yes' || v === 'true' || v === 'y';
}
function toggle(key: string) {
  fill.setValue(skey(key), isOn(key) ? 'no' : 'yes');
}
function setFlag(key: string, val: string) {
  fill.setValue(skey(key), val);
}
const needsInterpreter = computed(() => isOn('needs_interpreter'));
</script>

<template>
  <div class="allot-doc">
    <div class="doc-head" style="text-align:center">
      <div class="doc-meta">THE COMPANIES ACT 1967</div>
      <div class="doc-meta">SECTION 173C(a)</div>
      <div class="doc-lbl-title" style="margin:4px 0">
        CONSENT TO ACT AS DIRECTOR AND STATEMENT OF NON DISQUALIFICATION TO ACT AS DIRECTOR
      </div>
      <div class="doc-sec-title">FORM 45</div>
    </div>

    <p class="doc-p">Name of Company: <CddVarField field-key="company_name" label="Company Name" /></p>
    <p class="doc-p">Company No: <CddVarField field-key="company_registration_no" label="Company Registration No." /></p>

    <p class="doc-p">
      I, the undermentioned person, hereby consent to act as a director of the abovenamed company with effect from
      <CddVarField field-key="appointment_effective_date" label="Appointment Effective Date" /> (date) and declare that:
    </p>

    <p class="doc-p">(a)&nbsp;&nbsp;I am not disqualified from acting as a director, in that:</p>
    <p class="doc-p indent">(i)&nbsp;&nbsp;I am not below 18 years of age and that I am otherwise of full legal capacity.</p>
    <p class="doc-p indent">
      (ii)&nbsp;&nbsp;Within a period of 3 years preceding the date of this statement I have not had any disqualification
      order made against me under section 149A(1) of the Companies Act 1967 (&ldquo;the Act&rdquo;), section 60(1) of the
      LLP Act 2005 or section 57(1) of the VCC Act 2018.
    </p>
    <p class="doc-p indent">
      (iii)&nbsp;&nbsp;Within a period of 5 years preceding the date of this statement I have not had any disqualification
      order made against me under section 149(1) or 154(2) of the Act, section 59(1) or 61(2) of the LLP Act, or section
      56(1) or 58(2) of the VCC Act.
    </p>

    <!-- (iv) 1993년 이전 조항 — 조건부 토글 -->
    <p
      class="doc-p indent cond-row doc-toggle"
      :class="{ off: !isOn('has_pre_1993_history') }"
      title="Include the pre-12 Nov 1993 conviction declaration (iv)? Click to toggle."
      @click="toggle('has_pre_1993_history')"
    >
      <span class="cond-mark">{{ isOn('has_pre_1993_history') ? '☒' : '☐' }}</span>
      <span class="cond-txt">
        *(iv)&nbsp;&nbsp;That within a period of 5 years preceding 12th November 1993 I have not been convicted, whether
        within or without Singapore, of any offence (A) in connection with the promotion, formation or management of a
        corporation; (B) involving fraud or dishonesty punishable with imprisonment for 3 months or more; or (C) under
        section 157 of the Act; and
      </span>
    </p>

    <p class="doc-p indent">
      (v)&nbsp;&nbsp;That within a period of 5 years preceding the date of this statement, I have not been convicted /
      penalised / removed / prohibited as set out in sub-paragraphs (A)&ndash;(G) of section 173C(a)(v).
    </p>
    <p class="doc-p indent">
      (vi)&nbsp;&nbsp;That I am not otherwise disqualified as set out in sub-paragraphs (A)&ndash;(H) (offences/orders to
      file, undischarged bankruptcy, struck-off companies, debarment orders, VCC matters).
    </p>
    <p class="doc-p indent">
      (vii)&nbsp;&nbsp;By virtue of the foregoing I am not disqualified from acting as a director of the abovenamed company.
    </p>

    <p class="doc-p">
      (b)&nbsp;&nbsp;I am aware of and undertake to abide by my duties, responsibilities and liabilities specified in the
      Act as well as under the common law where applicable (discharge responsibilities; exercise reasonable skill and
      diligence; act honestly and avoid conflicts; use powers for proper purposes; ensure compliance under the Act;
      account to shareholders).
    </p>

    <!-- (ba) 노미니 디렉터 — 조건부 토글 -->
    <p
      class="doc-p cond-row doc-toggle"
      :class="{ off: !isOn('is_nominee_director') }"
      title="Nominee director declaration (ba)? Click to toggle."
      @click="toggle('is_nominee_director')"
    >
      <span class="cond-mark">{{ isOn('is_nominee_director') ? '☒' : '☐' }}</span>
      <span class="cond-txt">
        *(ba)&nbsp;&nbsp;I am acting as a nominee director of the abovenamed company by way of business, and I am a
        registered corporate service provider providing the corporate service of acting, or arranging for another person
        to act, as a director of a corporation; or my acting is arranged by a registered corporate service provider for
        that corporate service.
      </span>
    </p>

    <!-- (bb) Employment Pass — 조건부 토글 -->
    <p
      class="doc-p cond-row doc-toggle"
      :class="{ off: !isOn('is_employment_pass_holder') }"
      title="Employment Pass holder declaration (bb)? Click to toggle."
      @click="toggle('is_employment_pass_holder')"
    >
      <span class="cond-mark">{{ isOn('is_employment_pass_holder') ? '☒' : '☐' }}</span>
      <span class="cond-txt">
        *(bb)&nbsp;&nbsp;I am an Employment Pass holder and permission has been obtained from the Ministry of Manpower for
        me to take on this directorship.
      </span>
    </p>

    <p class="doc-p">
      (bc)&nbsp;&nbsp;By executing this form, I acknowledge that I am aware of my obligations under the Act and undertake
      to use this company only for legitimate business purposes in compliance with all applicable laws. I understand that
      any misuse of this company for unlawful purposes, including money laundering, fraud, scams or activities prejudicial
      to the public peace, welfare or good order in Singapore may result in regulatory or legal action.
    </p>

    <!-- (c) 읽음 vs 통역 — needs_interpreter 토글 -->
    <p class="doc-p">(c)&nbsp;&nbsp;That &mdash;</p>
    <p
      class="doc-p indent cond-row doc-toggle"
      :class="{ off: needsInterpreter }"
      title="I have read and understood (c)(i)"
      @click="setFlag('needs_interpreter', 'no')"
    >
      <span class="cond-mark">{{ !needsInterpreter ? '☒' : '☐' }}</span>
      <span class="cond-txt">*(i)&nbsp;&nbsp;I have read and understood the above statements; or</span>
    </p>
    <p
      class="doc-p indent cond-row doc-toggle"
      :class="{ off: !needsInterpreter }"
      title="The statements were interpreted to me (c)(ii)"
      @click="setFlag('needs_interpreter', 'yes')"
    >
      <span class="cond-mark">{{ needsInterpreter ? '☒' : '☐' }}</span>
      <span class="cond-txt">*(ii)&nbsp;&nbsp;the above statements were interpreted to me in</span>
    </p>

    <template v-if="needsInterpreter">
      <p class="doc-p indent">
        <CddVarField field-key="interpreter_language" label="Language / Dialect" /> (state language/dialect)
      </p>
      <p class="doc-p indent">
        by <CddVarField field-key="interpreter_name" label="Interpreter Name" /> (state name)
      </p>
      <p class="doc-p indent">
        NRIC NO: <CddVarField field-key="interpreter_nric" label="Interpreter NRIC" />
      </p>
    </template>

    <p class="doc-p">
      before I executed this form and I confirm that the statements are true. I am also aware that I can be prosecuted in
      Court if I willfully give any information on this form which is false.
    </p>

    <table class="cdd-tbl" style="margin-top:12px">
      <tbody>
        <tr>
          <td class="cdd-lcell">Name</td>
          <td><CddVarField field-key="director_name" label="Director Name" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Residential Address</td>
          <td><CddVarField field-key="director_residential_address" label="Director Residential Address" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Contact Address</td>
          <td><CddVarField field-key="director_contact_address" label="Director Contact Address" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">*NRIC/Passport No</td>
          <td><CddVarField field-key="director_identification_no" label="Director NRIC / Passport No." /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Nationality</td>
          <td><CddVarField field-key="director_nationality" label="Director Nationality" /></td>
        </tr>
      </tbody>
    </table>

    <!-- 서명 블록 [[Signers]] — 이 부수의 이사 본인(수동 추가/편집·T-Sign) -->
    <div class="sign-block">
      <div class="sign-lbl">Signature</div>
      <SgIncPreSigners name-key="director_name" title="Director" board-label="" />
    </div>

    <p class="doc-p" style="margin-top:10px">Dated this <CddVarField field-key="dated_on" label="Dated On" /></p>

    <div class="doc-attn"><p>* Delete where inapplicable.</p></div>
  </div>
</template>

<style scoped>
.indent { padding-left: 20px; }
.cond-row { display: flex; gap: 8px; align-items: flex-start; }
.cond-row.off { background: transparent; box-shadow: none; opacity: 0.55; }
.cond-mark { font-size: 15px; line-height: 1.3; flex: 0 0 auto; }
.cond-txt { flex: 1; }
.doc-attn p { margin: 0; }
.sign-block { margin-top: 14px; }
.sign-lbl { font-weight: 700; margin-bottom: 6px; }
</style>
